import React from 'react';
import { createRoot } from 'react-dom/client';
// First, before anything that reads the brand while it loads (src/lib/brandBoot.js).
import './src/lib/brandBoot.js';
import {
  BrowserRouter, Route, Routes, useNavigate, useParams,
} from 'react-router-dom';
import { ErrorBoundary, LoadingScreen, ToastProvider } from '@parsina/framework';

import { I18nProvider, useTranslation } from './src/i18n';
import { ShortcutProvider } from './src/lib/shortcuts';
import useMe from './src/lib/useMe';
import { accessOf, homePath } from './src/lib/access';
import AdminShell from './src/components/AdminShell';
import RequireAccess, { Home } from './src/components/RequireAccess';
import AuthGate from './src/components/AuthGate';
import SessionDialog from './src/components/SessionDialog';
import SystemState from './src/components/SystemState';
import Dashboard from './src/screens/Dashboard/Dashboard';
import Clinics from './src/screens/Clinics/Clinics';
import ClinicDetail from './src/screens/ClinicDetail/ClinicDetail';
import AiPricing from './src/screens/AiPricing/AiPricing';
import Marketplace from './src/screens/Marketplace/Marketplace';
import Whatsapp from './src/screens/Whatsapp/Whatsapp';
import DoctorPages from './src/screens/DoctorPages/DoctorPages';
import DemoRequests from './src/screens/DemoRequests/DemoRequests';
import Countries from './src/screens/Countries/Countries';
import CountryDetail from './src/screens/Countries/CountryDetail';
import PatientAccounts from './src/screens/PatientAccounts/PatientAccounts';
import { countryFromParam } from './src/constants/countryProfile';

function DetailRoute() {
  const navigate = useNavigate();
  const { clinicId } = useParams();
  // Keyed on the id so moving between two clinics remounts the screen rather
  // than showing the previous one's sections while the new query is in flight.
  return <ClinicDetail key={clinicId} clinicId={clinicId} navigate={navigate} />;
}

function CountryRoute() {
  const navigate = useNavigate();
  const { country } = useParams();
  // `/countries/fallback` is the `*` row; keyed like the clinic record.
  return <CountryDetail key={country} country={countryFromParam(country)} navigate={navigate} />;
}

function NotFound({ access }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const home = homePath(access);
  return (
    <SystemState
      code="404"
      titleKey="errors.system.notFound.title"
      adviceKey="errors.system.notFound.advice"
      testId="system-404"
      actions={home ? [{
        key: 'home',
        label: home === '/dashboard' ? t('common.actions.goToDashboard') : t('common.actions.goToDemoRequests'),
        onClick: () => navigate(home),
      }] : []}
    />
  );
}

function App() {
  const session = useMe();
  const { me } = session;
  const access = accessOf(session);
  return (
    <AdminShell me={me} access={access}>
      <Routes>
        <Route path="/" element={<Home access={access} />} />
        {/*
          The platform half. The guard is a LAYOUT route, so no screen under it
          mounts — and fires a query the BFF would refuse — until `me` says the
          caller holds platform_admin.
        */}
        <Route element={<RequireAccess access={access} need="platform" />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/clinics" element={<Clinics />} />
          <Route path="/clinics/:clinicId" element={<DetailRoute />} />
          <Route path="/ai-pricing" element={<AiPricing />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/whatsapp" element={<Whatsapp />} />
          <Route path="/doctor-pages" element={<DoctorPages />} />
          <Route path="/countries" element={<Countries />} />
          <Route path="/countries/:country" element={<CountryRoute />} />
          <Route path="/patient-accounts" element={<PatientAccounts me={me} />} />
        </Route>
        {/* The CRM half: the demo-request queue (docs/PARSINADENT_DEMO_REQUEST_DESIGN.md §7.2). */}
        <Route element={<RequireAccess access={access} need="crm" />}>
          <Route path="/demo-requests" element={<DemoRequests me={me} />} />
        </Route>
        {/*
          A real 404, not a silent redirect to the dashboard. A mistyped or stale
          link used to be swallowed, so an operator following an old link from a
          ticket landed on the dashboard and concluded the clinic was gone.
        */}
        <Route path="*" element={<NotFound access={access} />} />
      </Routes>
      <SessionDialog />
    </AdminShell>
  );
}

createRoot(document.getElementById('root')).render(
  /*
    The OUTER boundary, above every provider, with STATIC copy.

    It exists for exactly the case the inner one cannot cover: the i18n provider
    itself throwing. A fallback that called `t` there would throw again inside
    its own error path and produce the white page it was added to prevent — so
    this one's strings are literals, in the product's default language, and that
    is the one place in the app where a literal is correct.

    The inner boundary (in AdminShell, keyed on the pathname) is what makes a
    broken screen cost that screen instead of the session.
  */
  <ErrorBoundary
    // i18n-exempt: static copy for the boundary that catches the i18n provider.
    title="پنل بارگذاری نشد"
    body="صفحه را دوباره باز کنید. اگر تکرار شد، با پشتیبانی تماس بگیرید."
    retryLabel="تلاش مجدد"
    fullscreen
    testId="boot-error-boundary"
  >
    <I18nProvider fallback={<LoadingScreen />}>
      <ToastProvider>
        <BrowserRouter>
          <AuthGate>
            <ShortcutProvider>
              <App />
            </ShortcutProvider>
          </AuthGate>
        </BrowserRouter>
      </ToastProvider>
    </I18nProvider>
  </ErrorBoundary>,
);
